import { fetchWithAuth } from "@/hooks/useAuth";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  AlertTriangle,
  Calendar,
  Clock,
  Folder,
  HardDrive,
  RefreshCw,
} from "lucide-react";
import { toast } from "react-hot-toast";
import { Button } from "../../../components/ui/Button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../../../components/ui/Card";
import { Input } from "../../../components/ui/Input";
import { Skeleton } from "../../../components/ui/Skeleton";
import { Spinner } from "../../../components/ui/Spinner";
import { formatDate } from "../../../utils/format";
import { Select } from "@/components/ui/Select";

interface BackupSettings {
  enabled: boolean;
  frequency: "daily" | "weekly" | "monthly";
  retention: number;
  lastBackup: string | null;
  nextBackup: string | null;
  backupLocation: string;
}

function BackupSettingsTab({ selectedTab }: { selectedTab: string }) {
  const queryClient = useQueryClient();

  // Fetch backup settings
  const {
    data: backupSettings,
    isLoading,
    isError,
    error,
    refetch,
  } = useQuery({
    queryKey: ["backup-settings"],
    queryFn: async () => {
      const response = await fetchWithAuth("/api/admin/settings/backup");
      if (!response.ok)
        throw new Error("Eroare la încărcarea setărilor de backup");
      return response.json() as Promise<BackupSettings>;
    },
    enabled: selectedTab === "backup",
  });

  // Update backup settings mutation
  const updateBackupSettings = useMutation({
    mutationFn: async (data: Partial<BackupSettings>) => {
      const response = await fetchWithAuth("/api/admin/settings/backup", {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      });
      if (!response.ok)
        throw new Error("Eroare la actualizarea setărilor de backup");
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["backup-settings"] });
      toast.success("Setări de backup actualizate cu succes");
    },
    onError: (error: Error) => {
      toast.error(error.message);
    },
  });

  // Trigger manual backup mutation
  const triggerBackup = useMutation({
    mutationFn: async () => {
      const response = await fetchWithAuth(
        "/api/admin/settings/backup/trigger",
        {
          method: "POST",
        }
      );
      if (!response.ok) throw new Error("Eroare la inițierea backup-ului");
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["backup-settings"] });
      toast.success("Backup inițiat cu succes");
    },
    onError: (error: Error) => {
      toast.error(error.message);
    },
  });

  if (isLoading) {
    return (
      <Card className="">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <HardDrive className="w-5 h-5" />
            Setări Backup
          </CardTitle>
        </CardHeader>
        <CardContent>
          {/* Checkbox row skeleton */}
          <div className="flex items-center gap-2 p-2 rounded-lg">
            <Skeleton className="h-5 w-5 rounded" />
            <Skeleton className="h-4 w-56" />
          </div>
          {/* Frequency select */}
          <div className="mt-4">
            <Skeleton className="h-4 w-40 mb-2" />
            <Skeleton className="h-10 w-full" />
          </div>
          {/* Retention input */}
          <div className="mt-4">
            <Skeleton className="h-4 w-64 mb-2" />
            <Skeleton className="h-10 w-full" />
          </div>
          {/* Location input */}
          <div className="mt-4">
            <Skeleton className="h-4 w-56 mb-2" />
            <Skeleton className="h-10 w-full" />
          </div>
          {/* Backup timestamps */}
          <div className="mt-4 space-y-3">
            <Skeleton className="h-4 w-40" />
            <Skeleton className="h-4 w-44" />
          </div>
          {/* Button skeleton */}
          <div className="mt-6">
            <Skeleton className="h-10 w-44" />
          </div>
        </CardContent>
      </Card>
    );
  }

  if (isError) {
    return (
      <Card className="">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-red-500" />
            Eroare la încărcarea setărilor de backup
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-between">
            <div className="text-sm text-muted-foreground">
              {(error as Error)?.message || "A apărut o eroare neașteptată."}
            </div>
            <Button
              variant="secondary"
              onClick={() => refetch()}
              className="ml-4"
            >
              Reîncearcă
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!backupSettings) return null;

  return (
    <Card className="">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <HardDrive className="w-5 h-5" />
          Setări Backup
        </CardTitle>
      </CardHeader>
      <CardContent>
        <form className="space-y-4">
          <div className="flex items-center gap-2 p-2 rounded-lg hover:bg-gray-50">
            <input
              type="checkbox"
              id="backupEnabled"
              checked={backupSettings.enabled}
              onChange={(e) =>
                updateBackupSettings.mutate({
                  enabled: e.target.checked,
                })
              }
              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            />
            <label
              htmlFor="backupEnabled"
              className="text-sm font-medium text-muted-foreground flex items-center gap-2"
            >
              <RefreshCw className="w-4 h-4" />
              Backup Automat
            </label>
          </div>
          <div>
            <label
              htmlFor="backupFrequency"
              className="text-sm font-medium text-muted-foreground flex items-center gap-2"
            >
              <Clock className="w-4 h-4" />
              Frecvență
            </label>
            <Select
              value={backupSettings.frequency}
              onValueChange={(value) =>
                updateBackupSettings.mutate({
                  frequency: value as "daily" | "weekly" | "monthly",
                })
              }
            >
              <option value="daily">Zilnic</option>
              <option value="weekly">Săptămânal</option>
              <option value="monthly">Lunar</option>
            </Select>
          </div>
          <div>
            <label
              htmlFor="backupRetention"
              className="text-sm font-medium text-muted-foreground flex items-center gap-2"
            >
              <Calendar className="w-4 h-4" />
              Retenție (număr de backup-uri)
            </label>
            <Input
              id="backupRetention"
              type="number"
              value={backupSettings.retention}
              onChange={(e) =>
                updateBackupSettings.mutate({
                  retention: Number(e.target.value),
                })
              }
              className="mt-1"
            />
          </div>
          <div>
            <label
              htmlFor="backupLocation"
              className="text-sm font-medium text-muted-foreground flex items-center gap-2"
            >
              <Folder className="w-4 h-4" />
              Locație Backup
            </label>
            <Input
              id="backupLocation"
              value={backupSettings.backupLocation}
              onChange={(e) =>
                updateBackupSettings.mutate({
                  backupLocation: e.target.value,
                })
              }
              className="mt-1"
            />
          </div>
          <div>
            <div className="text-sm font-medium">Ultimul Backup</div>
            <div>
              {backupSettings.lastBackup
                ? formatDate(backupSettings.lastBackup)
                : "N/A"}
            </div>
          </div>
          <div>
            <div className="text-sm font-medium">Următorul Backup</div>
            <div>
              {backupSettings.nextBackup
                ? formatDate(backupSettings.nextBackup)
                : "N/A"}
            </div>
          </div>
          <Button
            onClick={() => triggerBackup.mutate()}
            disabled={triggerBackup.isPending}
            className="flex items-center gap-2"
          >
            {triggerBackup.isPending ? (
              <>
                <Spinner className="w-4 h-4" />
                Se execută...
              </>
            ) : (
              <>
                <RefreshCw className="w-4 h-4" />
                Backup Manual
              </>
            )}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
}

export default BackupSettingsTab;
